import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { LoQueEspera } from '@/lib/supabase/tipos-bd'
import { VistaPreviaCotizacion } from './VistaPreviaCotizacion'
import { pesos } from '@/shared/utilidades/formato'

import { duracion } from './duracion'
import { DECIDIBLES, IR_AL_DOCUMENTO } from './tiposDeDecision'
import { HORAS_ALARMA, HORAS_AVISO } from './useTablero'
import {
  useAutorizarInicio,
  useHorasPedidas,
  useDecidirCotizacion,
  useLoQueEspera,
  useSolicitarMasTiempoDesdeElTablero,
} from './useLoQueEspera'
import { useAsignarRecogida } from '@/modules/campo/useRecogidas'
import { useColaboradoresActivos, useDecidirTraslado } from '@/modules/programacion/useHerramientas'
import estilos from './DecisionesRapidas.module.css'

/**
 * Decisiones rápidas.
 *
 * El tablero no repite la matriz. La matriz es donde se trabaja un documento
 * —se arma una cotización, se programa una orden—; el tablero es donde se
 * resuelve lo que se decide CON UN SÍ O UN NO, sin abrir nada.
 *
 * Por eso aquí solo salen dos cosas: la cotización que espera aprobación y el
 * colaborador que pide más tiempo. Lo demás que está detenido se cuenta
 * arriba, en las cifras, y se atiende en la matriz, que es su sitio.
 */

/** Las que se resuelven sin salir de aquí, con un sí o un no. */

/**
 * Las que SÍ hay que abrir, pero que igual merecen su carta aquí.
 *
 * Un trabajo terminado esperando revisión no se resuelve con un sí o un no: hay
 * que leer lo que reportó el técnico y ver sus fotos antes de cerrarlo, porque
 * cerrarlo es mandarle el acta al cliente para que firme. Pero enterarse de que
 * existe no debería costar entrar a buscarlo: antes solo salía como un número
 * en las cifras de arriba. La carta avisa; el botón lleva a donde se revisa.
 */

/** Cómo se llama cada montón, en plural y en lenguaje de la casa. */
const GRUPOS: Record<string, string> = {
  aprobar_cotizacion: 'Cotizaciones por aprobar',
  precierre: 'Trabajos por revisar',
  mas_tiempo: 'Piden más horas',
  inicio_sin_ubicacion: 'Piden empezar sin ubicación',
  traslado_herramienta: 'Traslados de herramienta',
  recogida_herramienta: 'Herramienta por recoger',
}

/** El orden en que se miran: primero la plata, después la obra. */
const ORDEN_GRUPOS = [
  'aprobar_cotizacion',
  'precierre',
  'mas_tiempo',
  'inicio_sin_ubicacion',
  'traslado_herramienta',
  'recogida_herramienta',
]

/** Cómo se llama cada decisión y qué dicen sus dos botones. */
const ETIQUETAS: Record<string, { marca: string; si: string; no: string }> = {
  // «Aprobar» a secas mentía desde 0123: aprobar ya no cierra nada, le manda
  // la cotización al cliente para que la acepte él.
  aprobar_cotizacion: { marca: 'Aprobación', si: 'Aprobar y enviar', no: 'Rechazar' },
  precierre: { marca: 'Por revisar', si: 'Revisar y cerrar', no: '' },
  mas_tiempo: { marca: 'Más tiempo', si: 'Dar las horas', no: 'Negar' },
  inicio_sin_ubicacion: { marca: 'Sin ubicación', si: 'Dejar empezar', no: 'Negar' },
  traslado_herramienta: { marca: 'Traslado', si: 'Aprobar traslado', no: 'Rechazar' },
  // No hay «no»: una herramienta afuera hay que ir a buscarla. Lo que se
  // decide es quién.
  recogida_herramienta: { marca: 'Recogida', si: 'Asignar quién va', no: '' },
}

export function DecisionesRapidas() {
  const { pendientes, cargando } = useLoQueEspera()

  const decidibles = pendientes.filter((p) => DECIDIBLES.has(p.tipo) || IR_AL_DOCUMENTO.has(p.tipo))

  if (cargando) return <p className={estilos.estado}>Buscando…</p>

  if (decidibles.length === 0) {
    return (
      <p className={estilos.despejado}>
        <span className={estilos.despejadoCifra}>0</span>
        Nada esperando una decisión suya.
      </p>
    )
  }

  /*
   * AGRUPADAS POR TIPO, NO PINTADAS POR TIPO.
   *
   * Johan pidió poder distinguir de un vistazo una cotización de un cierre. El
   * color no servía: aquí YA significa cuánto lleva esperando —rojo a los tres
   * días, naranja al día, verde recién llegada— y darle un segundo significado
   * haría que un mismo color dijera dos cosas.
   *
   * Separarlas por estructura lo resuelve sin gastar ese canal: cada tipo bajo
   * su propio encabezado, con su cuenta. Se distinguen, se saltan en bloque, y
   * el color sigue contando lo único que contaba.
   */
  const grupos = ORDEN_GRUPOS.map((tipo) => ({
    tipo,
    titulo: GRUPOS[tipo],
    items: decidibles.filter((p) => p.tipo === tipo),
  })).filter((g) => g.items.length > 0)

  return (
    <div className={estilos.grupos}>
      {grupos.map((g) => (
        // Cada grupo con su color: título, franja de las tarjetas, etiqueta
        // y botón (ver el CSS, «UN COLOR POR GRUPO»).
        <section key={g.tipo} className={estilos[`grupo_${g.tipo}`]}>
          <p className={estilos.tituloGrupo}>
            {g.titulo}
            <span className={estilos.cuantos}>{g.items.length}</span>
          </p>

          <ul className={estilos.lista}>
            {g.items.map((item) => (
              <Fila key={`${item.tipo}-${item.referencia_id}`} item={item} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Los mismos umbrales del resto del tablero, importados y no copiados.
 *
 * Estaban escritos a mano —24 y 72— y al subir los del tablero se habrían
 * quedado atrás: la misma espera se habría visto verde arriba y naranja aquí.
 */
function tonoDe(horas: number): string {
  if (horas >= HORAS_ALARMA) return estilos.alarma
  if (horas >= HORAS_AVISO) return estilos.aviso
  return estilos.reciente
}

function Fila({ item }: { item: LoQueEspera }) {
  const navegar = useNavigate()
  const decidirCotizacion = useDecidirCotizacion()
  const decidirTiempo = useSolicitarMasTiempoDesdeElTablero()
  const autorizarInicio = useAutorizarInicio()
  const decidirTraslado = useDecidirTraslado()
  const asignarRecogida = useAsignarRecogida()
  const esRecogida = item.tipo === 'recogida_herramienta'
  const { colaboradores } = useColaboradoresActivos()
  // Primero «Asignar quién va» y entonces sale la lista, como en las horas.
  const [asignando, setAsignando] = useState(false)
  const [quienVa, setQuienVa] = useState('')

  const [negando, setNegando] = useState(false)
  const [notas, setNotas] = useState('')
  const [error, setError] = useState<string | null>(null)

  // duracion() devuelve «10,0 días» o «21,2 h»: se parte para poder componer
  // la cifra en grande y la unidad en pequeño.
  const texto = duracion(item.horas_esperando) ?? '—'
  const corte = texto.lastIndexOf(' ')
  const numero = corte === -1 ? texto : texto.slice(0, corte)
  const unidad = corte === -1 ? '' : texto.slice(corte + 1)

  const esCotizacion = item.tipo === 'aprobar_cotizacion'
  const [viendo, setViendo] = useState(false)

  // MÁS TIEMPO: cuántas pide y cuántas se le dan. Se abre un campo con las
  // pedidas ya puestas; se cambia si se le quieren dar menos (o más).
  const esMasTiempo = item.tipo === 'mas_tiempo'
  const { data: horasPedidas = null } = useHorasPedidas(esMasTiempo ? item.referencia_id : null)
  // Primero «Dar las horas» y entonces sale el campo, arriba a la derecha.
  const [dando, setDando] = useState(false)
  // Vacío = las que pidió. Lo que se escriba manda.
  const [horasQueSeDan, setHorasQueSeDan] = useState('')
  const horasEscritas =
    horasQueSeDan !== '' ? horasQueSeDan : horasPedidas !== null ? String(horasPedidas) : ''
  const horasADar = Number(horasEscritas.replace(',', '.'))
  const etiqueta = ETIQUETAS[item.tipo]
  const trabajando =
    decidirCotizacion.isPending ||
    decidirTiempo.isPending ||
    autorizarInicio.isPending ||
    decidirTraslado.isPending ||
    asignarRecogida.isPending

  async function asignar() {
    setError(null)
    try {
      await asignarRecogida.mutateAsync({ recogidaId: item.referencia_id, colaboradorId: quienVa })
      setAsignando(false)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible asignarla.')
    }
  }

  async function resolver(aprobar: boolean) {
    setError(null)

    try {
      if (esCotizacion) {
        await decidirCotizacion.mutateAsync({
          cotizacionId: item.referencia_id,
          aprobar,
          notas: notas.trim() || null,
        })
      } else if (item.tipo === 'traslado_herramienta') {
        await decidirTraslado.mutateAsync({
          trasladoId: item.referencia_id,
          aprobar,
          notas: notas.trim() || undefined,
        })
      } else if (item.tipo === 'inicio_sin_ubicacion') {
        await autorizarInicio.mutateAsync({
          ordenTrabajoId: item.referencia_id,
          aprobar,
          notas: notas.trim() || null,
        })
      } else {
        await decidirTiempo.mutateAsync({
          ordenTrabajoId: item.referencia_id,
          aprobar,
          notas: notas.trim() || null,
          horas: aprobar && horasADar > 0 ? horasADar : null,
        })
        setDando(false)
      }

      setNegando(false)
      setNotas('')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible registrar la decisión.')
    }
  }

  return (
    <li
      className={`${estilos.fila} ${tonoDe(item.horas_esperando)} ${
        viendo || negando ? estilos.filaAbierta : ''
      }`}
    >
      {/* EL TICKET. Johan: «todas las cards son así… súper básico, ¿podrías
          buscar algo con más diseño?». Ya no es una caja con una franja de
          color: arriba qué es (con el punto del grupo) y cuánto lleva (en el
          color de la urgencia); en medio a quién y la cifra que se decide, en
          grande; abajo, en su propio pie, lo que se hace. */}
      <div className={estilos.ticketTope}>
        <span className={estilos.tipo}>
          <i className={estilos.tipoPunto} aria-hidden="true" />
          {etiqueta.marca}
        </span>
        <span className={estilos.espera} title="Tiempo esperando">
          <span className={estilos.cifra}>{numero}</span> {unidad}
        </span>
      </div>

      <div className={estilos.datos}>
        <p className={estilos.cliente}>{item.cliente ?? 'Sin cliente'}</p>
        <p className={estilos.referencias}>
          {/* En una recogida el documento es la etiqueta de la herramienta y
              puede no haber orden detrás: entonces no es un enlace. */}
          {item.orden_servicio_id ? (
            <button
              type="button"
              className={estilos.documento}
              onClick={() => navegar(`/ordenes/${item.orden_servicio_id}`)}
            >
              {item.documento}
            </button>
          ) : (
            <span className={`${estilos.documento} ${estilos.documentoSinEnlace}`}>
              {item.documento}
            </span>
          )}
          {esCotizacion && item.detalle && <span>{item.detalle}</span>}
        </p>

        {/* La cifra que se decide, en grande: la plata en una cotización. */}
        {esCotizacion && item.valor !== null && (
          <p className={estilos.monto}>{pesos(item.valor)}</p>
        )}
        {!esCotizacion && !esMasTiempo && item.detalle && (
          // En un traslado el detalle ES la decisión —qué, a quién, por qué—:
          // va completo, sin cortarse en dos renglones.
          <p
            className={`${estilos.detalle} ${
              item.tipo === 'traslado_herramienta' || esRecogida ? estilos.detalleCompleto : ''
            }`}
          >
            {item.detalle}
          </p>
        )}
        {esMasTiempo && item.detalle && <p className={estilos.detalle}>{item.detalle}</p>}

        {/* CUÁNTAS PIDE Y CUÁNTAS SE DAN, EN LA MISMA TARJETA. Johan: «¿dónde
            puedo dar las horas que yo piense?», y después, señalando el hueco
            a la derecha: «¿y por qué no ahí?» —con el paso intermedio: primero
            «Dar las horas», y entonces el campo sale ahí, junto a lo que
            pidió—. Trae las pedidas y se escribe encima; el botón dice
            cuántas va a dar. */}
        {esMasTiempo && (
          <div className={estilos.pideYDa}>
            <span className={estilos.monto}>Pide {horasPedidas ?? '…'} h</span>
            {dando && (
              <label className={estilos.horasQueSeDan}>
                {/* Sin rótulo largo: cabe junto a lo que pidió sin abrir la
                    tarjeta (Johan: «si acá cabe, ¿para qué se expande?»). */}
                <input
                  type="number"
                  inputMode="decimal"
                  min="0.5"
                  step="0.5"
                  value={horasEscritas}
                  onChange={(e) => setHorasQueSeDan(e.target.value)}
                  aria-label="Horas que se le dan"
                  autoFocus
                />
                h
              </label>
            )}
          </div>
        )}

        {/* Negar exige decir por qué, así que el campo aparece al negar y no
            antes: pedir la razón de una decisión que no se ha tomado estorba. */}
        {negando && (
          <textarea
            rows={2}
            className={estilos.notas}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder={`Por qué se responde que no`}
            autoFocus
          />
        )}

        {/* QUIÉN VA POR ELLA. Al operario le llega un aviso por WhatsApp y
            la ve en «Recogidas a tu cargo»; la entrega es escaneándola en
            bodega. */}
        {esRecogida && asignando && (
          <select
            className={estilos.notas}
            value={quienVa}
            onChange={(e) => setQuienVa(e.target.value)}
            aria-label="Quién va por ella"
            autoFocus
          >
            <option value="">¿Quién va por ella?</option>
            {colaboradores.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
                {c.cargo ? ` · ${c.cargo}` : ''}
              </option>
            ))}
          </select>
        )}

        {error && <p className={estilos.error}>{error}</p>}
      </div>

      <div className={estilos.acciones}>
        {IR_AL_DOCUMENTO.has(item.tipo) ? (
          <button
            type="button"
            className={estilos.principal}
            onClick={() => navegar(`/ordenes/${item.orden_servicio_id}`)}
          >
            {etiqueta.si}
          </button>
        ) : esRecogida ? (
          asignando ? (
            <>
              <button
                type="button"
                className={estilos.secundario}
                onClick={() => {
                  setAsignando(false)
                  setQuienVa('')
                }}
              >
                Volver
              </button>
              <button
                type="button"
                className={estilos.principal}
                onClick={() => void asignar()}
                disabled={trabajando || !quienVa}
              >
                {quienVa
                  ? `Mandar a ${
                      colaboradores.find((c) => c.id === quienVa)?.nombre.split(' ')[0] ?? ''
                    }`
                  : 'Asignar'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={estilos.principal}
              onClick={() => setAsignando(true)}
              disabled={trabajando}
            >
              {etiqueta.si}
            </button>
          )
        ) : dando ? (
          <>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => {
                setDando(false)
                setHorasQueSeDan('')
              }}
            >
              Volver
            </button>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void resolver(true)}
              disabled={trabajando || !(horasADar > 0)}
            >
              Dar {horasEscritas || '…'} h
            </button>
          </>
        ) : negando ? (
          <>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => {
                setNegando(false)
                setNotas('')
              }}
            >
              Volver
            </button>

            <button
              type="button"
              className={estilos.peligro}
              onClick={() => void resolver(false)}
              disabled={notas.trim().length < 10 || trabajando}
            >
              Confirmar
            </button>
          </>
        ) : (
          <>
            {/* MIRAR ANTES DE APROBAR. Aprobar manda el precio al cliente; la
                carta sola no enseña ni un renglón. */}
            {esCotizacion && (
              <button
                type="button"
                className={estilos.secundario}
                onClick={() => setViendo((v) => !v)}
                disabled={trabajando}
              >
                {viendo ? 'Ocultar' : 'Ver'}
              </button>
            )}

            <button
              type="button"
              className={estilos.secundario}
              onClick={() => setNegando(true)}
              disabled={trabajando}
            >
              {etiqueta.no}
            </button>

            {/* En más tiempo no da a ciegas: abre el campo de las horas. */}
            <button
              type="button"
              className={estilos.principal}
              onClick={() => (esMasTiempo ? setDando(true) : void resolver(true))}
              disabled={trabajando}
            >
              {etiqueta.si}
            </button>
          </>
        )}
      </div>

      {viendo && item.orden_servicio_id && (
        <div className={estilos.previa}>
          <VistaPreviaCotizacion ordenServicioId={item.orden_servicio_id} />
        </div>
      )}
    </li>
  )
}
